import { WEB_CLIENT_TYPE } from '@constants/index';
import { useGeneralStore } from 'src/store';

/**
 * "W2 host" detection + the token-less-B2C route lockdown decision.
 *
 * A W2 host is a group whose `webClientType` resolves to the W2/small-employer
 * tenant kind (virgil `WebClientType.SmallEmployerTenant` = 3). The brand/group
 * is resolved by hostname at boot (`useBranding`) and stored on
 * `useGeneralStore().siteConfig.webClientType`; this module is the ONE place that
 * reads it for the lockdown gate.
 *
 * On a W2 host every route is BLOCKED except the small allowlist below — a W2
 * employee must not be able to reach any B2C surface that could create B2C state
 * (draft applications, subscriptions) on the shared W2 group. B2C hosts (any
 * other `webClientType`, or an unresolvable host) are COMPLETELY unaffected.
 */

export function isW2WebClientType(webClientType: number | undefined): boolean {
  return webClientType === WEB_CLIENT_TYPE.SMALL_EMPLOYER_TENANT;
}

/** Reactive (render-time) W2-host read — re-subscribes to config changes. */
export function useIsW2Host(): boolean {
  const webClientType = useGeneralStore((s) => s.siteConfig?.webClientType);
  return isW2WebClientType(webClientType);
}

/**
 * Imperative W2-host read for event handlers / effects / non-component code
 * (e.g. the post-login redirect). Never call this during React render — it won't
 * re-subscribe; use {@link useIsW2Host} there.
 */
export function isW2HostNow(): boolean {
  return isW2WebClientType(
    useGeneralStore.getState().siteConfig?.webClientType
  );
}

/**
 * The lockdown allowlist. Everything NOT matched here is blocked on a W2 host.
 * Kept as visible constants so the policy is auditable in one glance.
 */
export const W2_ALLOWED_EXACT_PATHS = [
  '/login',
  '/forgot-password',
  '/reset-password',
  // Returning-employee self-selection surface (guards on its own JWT).
  '/select-plans',
] as const;

/** Prefix-matched allowlist: the tokenized hosted pages and all their children. */
export const W2_ALLOWED_PATH_PREFIXES = [
  // Tokenized employee flow: `/e`, `/e/<token>`, `/e/<token>/register`, …
  '/e',
  // Public contract-signing page: `/contract` and `/contract/<token>`.
  '/contract',
] as const;

/**
 * Is `pathname` (with its `search`) reachable on a W2 host?
 *
 * Special case: the bare landing `/` is blocked UNLESS it carries the legacy
 * `?t=<token>` param — that presence means the `W2RootGate` shim is about to
 * redirect it into `/e/<token>`, so it must stay allowed for the redirect to fire.
 */
export function isW2AllowedPath(pathname: string, search: string): boolean {
  if (pathname === '/') {
    return new URLSearchParams(search).has('t');
  }
  if ((W2_ALLOWED_EXACT_PATHS as readonly string[]).includes(pathname)) {
    return true;
  }
  return (W2_ALLOWED_PATH_PREFIXES as readonly string[]).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export interface W2GateInput {
  webClientType: number | undefined;
  /** True while the brand/group is still resolving (webClientType not yet known). */
  isLoadingConfig: boolean;
  /**
   * True once a branding fetch FAILED and left the host type unresolved (no
   * persisted `siteConfig`). Drives the fail-closed branch below.
   */
  configLoadFailed: boolean;
  pathname: string;
  search: string;
}

/**
 * What the route-level gate should render for this request:
 *   - `allow`   → mount the requested route tree unchanged;
 *   - `block`   → replace it with the branded block screen (outside allowlist);
 *   - `loading` → mount NEITHER — render a neutral loading shell so no route's
 *                 effects run until the host type is known (finding #2: the
 *                 boundary must prevent mount, not merely hide after resolution).
 */
export type W2GateDecision = 'allow' | 'block' | 'loading';

/**
 * The gate decision (pure, unit-tested). Host type is known either from a
 * freshly resolved branding fetch OR from the persisted `siteConfig` a returning
 * browser rehydrates synchronously at boot — so a returning W2 browser is locked
 * down IMMEDIATELY, before any fresh fetch settles.
 *
 * - Known W2 host → block everything outside the allowlist (even mid-fetch).
 * - Known non-W2 host → allow (genuine B2C, completely unaffected).
 * - Host UNRESOLVED and still loading → loading shell (don't mount yet).
 * - Host UNRESOLVED after a fetch FAILURE → fail CLOSED (block outside the
 *   allowlist) rather than leave a possibly-W2 host open. A build with no group
 *   hash never sets `configLoadFailed`, so local/dev B2C stays unaffected.
 */
export function w2GateDecision(input: W2GateInput): W2GateDecision {
  const outsideAllowlist = (): W2GateDecision =>
    isW2AllowedPath(input.pathname, input.search) ? 'allow' : 'block';

  if (isW2WebClientType(input.webClientType)) return outsideAllowlist();
  // Some other, resolved host type → genuine B2C, never gated.
  if (input.webClientType !== undefined) return 'allow';
  // Host type is unknown here.
  if (input.isLoadingConfig) return 'loading';
  if (input.configLoadFailed) return outsideAllowlist();
  return 'allow';
}

/**
 * Boolean convenience over {@link w2GateDecision}: is this route BLOCKED?
 * (A `loading` decision is not a block.)
 */
export function shouldBlockW2Route(input: W2GateInput): boolean {
  return w2GateDecision(input) === 'block';
}
